import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Divider,
  makeStyles,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  FileCopy,
  Close as CloseIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  subtitleText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: "1.05rem",
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.type === "dark" ? "#64b5f6" : "#1877F2",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  fieldLabel: {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginBottom: 4,
    color: theme.palette.text.primary,
  },
  subLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: 4,
    fontWeight: 400,
  },
  redirectBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#f1f5f9",
    borderRadius: 6,
    padding: "10px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#cbd5e1"}`,
    fontFamily: "monospace",
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    marginTop: 4,
  },
  redirectSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  formControl: {
    width: "100%",
  },
  blockDivider: {
    margin: "24px 0",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
  },
}));

const API_VERSIONS = [
  "v17.0",
  "v18.0",
  "v19.0",
  "v20.0",
  "v21.0",
  "v22.0",
  "v23.0",
  "v24.0",
  "v25.0",
  "custom",
];

export const WabaForm = ({ companyId, onSaved, hideSaveButton = false }) => {
  const classes = useStyles();

  // WhatsApp WABA
  const [appId, setAppId] = useState("");
  const [apiVersion, setApiVersion] = useState("v20.0");
  const [customApiVersion, setCustomApiVersion] = useState("");
  const [isCustomVersion, setIsCustomVersion] = useState(false);
  const [description, setDescription] = useState("");
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [configId, setConfigId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [showAppSecret, setShowAppSecret] = useState(false);

  // Instagram
  const [instagramAppId, setInstagramAppId] = useState("");
  const [instagramAppSecret, setInstagramAppSecret] = useState("");
  const [showInstagramAppSecret, setShowInstagramAppSecret] = useState(false);

  // Facebook
  const [facebookAppId, setFacebookAppId] = useState("");
  const [facebookAppSecret, setFacebookAppSecret] = useState("");
  const [showFacebookAppSecret, setShowFacebookAppSecret] = useState(false);

  const [loading, setLoading] = useState(false);

  const redirectUriWaba = `${window.location.origin}/waba-signup`;
  const redirectUriInstagram = `${window.location.origin}/instagram-signup`;
  const redirectUriFacebook = `${window.location.origin}/facebook-signup`;

  useEffect(() => {
    fetchSettings();
  }, [companyId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      if (Array.isArray(data)) {
        const findVal = (key) => {
          const item = data.find((s) => s.key === key);
          return item ? item.value : "";
        };

        // WhatsApp WABA
        setAppId(findVal("wabaAppId") || findVal("metaAppId"));
        const version = findVal("wabaApiVersion") || "v20.0";
        if (API_VERSIONS.includes(version)) {
          setApiVersion(version);
          setIsCustomVersion(false);
        } else {
          setApiVersion("custom");
          setCustomApiVersion(version);
          setIsCustomVersion(true);
        }
        setDescription(findVal("wabaDescription"));
        setToken(findVal("wabaToken"));
        setConfigId(findVal("wabaConfigId") || findVal("metaConfigId"));
        setAppSecret(findVal("wabaAppSecret") || findVal("metaAppSecret"));

        // Instagram
        setInstagramAppId(findVal("instagramAppId") || findVal("metaInstagramAppId"));
        setInstagramAppSecret(findVal("instagramAppSecret") || findVal("metaInstagramAppSecret"));

        // Facebook
        setFacebookAppId(findVal("facebookAppId") || findVal("metaFacebookAppId"));
        setFacebookAppSecret(findVal("facebookAppSecret") || findVal("metaFacebookAppSecret"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (e) => {
    const val = e.target.value;
    if (val === "custom") {
      setIsCustomVersion(true);
      setApiVersion("custom");
    } else {
      setIsCustomVersion(false);
      setApiVersion(val);
    }
  };

  const handleCopyUri = (uri, type) => {
    navigator.clipboard.writeText(uri);
    toast.success(`URI de redirecionamento do ${type} copiada!`);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const finalVersion = isCustomVersion ? customApiVersion : apiVersion;

      const settingsToSave = [
        // WhatsApp WABA
        { key: "wabaAppId", value: appId },
        { key: "metaAppId", value: appId },
        { key: "wabaApiVersion", value: finalVersion },
        { key: "wabaDescription", value: description },
        { key: "wabaToken", value: token },
        { key: "wabaConfigId", value: configId },
        { key: "metaConfigId", value: configId },
        { key: "wabaAppSecret", value: appSecret },
        { key: "metaAppSecret", value: appSecret },

        // Instagram
        { key: "instagramAppId", value: instagramAppId },
        { key: "metaInstagramAppId", value: instagramAppId },
        { key: "instagramAppSecret", value: instagramAppSecret },
        { key: "metaInstagramAppSecret", value: instagramAppSecret },

        // Facebook
        { key: "facebookAppId", value: facebookAppId },
        { key: "metaFacebookAppId", value: facebookAppId },
        { key: "facebookAppSecret", value: facebookAppSecret },
        { key: "metaFacebookAppSecret", value: facebookAppSecret },
      ];

      for (const item of settingsToSave) {
        await api.put(`/settings/${item.key}`, {
          value: item.value,
        });
      }

      toast.success("Informações da Meta salvas com sucesso!");
      if (onSaved) onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      {/* ── BLOCO 1: WHATSAPP OFICIAL (WABA) ── */}
      <Grid item xs={12}>
        <div className={classes.sectionTitle}>
          🟢 Configurações WhatsApp Oficial (WABA)
        </div>
      </Grid>

      {/* App ID & Versao API */}
      <Grid item xs={12} sm={7}>
        <div className={classes.fieldLabel}>App ID</div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="ID do aplicativo"
          value={appId}
          onChange={(e) => setAppId(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={5}>
        <div className={classes.fieldLabel}>Versão API</div>
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Select value={apiVersion} onChange={handleVersionChange}>
            {API_VERSIONS.map((ver) => (
              <MenuItem key={ver} value={ver}>
                {ver === "custom" ? "Inserir manualmente" : ver}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {isCustomVersion && (
        <Grid item xs={12}>
          <div className={classes.fieldLabel}>Digite a versão personalizada da API</div>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ex: v26.0"
            value={customApiVersion}
            onChange={(e) => setCustomApiVersion(e.target.value)}
          />
        </Grid>
      )}

      {/* Descrição */}
      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Descrição</div>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          size="small"
          placeholder="Descrição do aplicativo WABA"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Grid>

      {/* Token */}
      <Grid item xs={12}>
        <div className={classes.fieldLabel}>
          Token <span className={classes.subLabel}>(Token permanente de acesso)</span>
        </div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type={showToken ? "text" : "password"}
          placeholder="••••••••••••••••"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowToken(!showToken)} edge="end" size="small">
                  {showToken ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Config ID */}
      <Grid item xs={12}>
        <div className={classes.fieldLabel}>
          Config ID <span className={classes.subLabel}>(ID da configuração do webhook / login)</span>
        </div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="ID de configuração"
          value={configId}
          onChange={(e) => setConfigId(e.target.value)}
        />
      </Grid>

      {/* App Secret */}
      <Grid item xs={12}>
        <div className={classes.fieldLabel}>
          App Secret <span className={classes.subLabel}>(Chave secreta do aplicativo)</span>
        </div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type={showAppSecret ? "text" : "password"}
          placeholder="App Secret"
          value={appSecret}
          onChange={(e) => setAppSecret(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowAppSecret(!showAppSecret)} edge="end" size="small">
                  {showAppSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Redirect URI WABA */}
      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Redirect URI WhatsApp</div>
        <div className={classes.redirectBox}>
          <Box style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {redirectUriWaba}
          </Box>
          <IconButton size="small" onClick={() => handleCopyUri(redirectUriWaba, "WhatsApp")}>
            <FileCopy fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.redirectSub}>URI de redirecionamento para OAuth WhatsApp WABA</div>
      </Grid>

      {/* ── DIVISOR ── */}
      <Grid item xs={12}>
        <Divider className={classes.blockDivider} />
      </Grid>

      {/* ── BLOCO 2: INSTAGRAM ── */}
      <Grid item xs={12}>
        <div className={classes.sectionTitle}>
          🟣 Configurações Instagram
        </div>
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Instagram App ID</div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="ID do aplicativo Instagram"
          value={instagramAppId}
          onChange={(e) => setInstagramAppId(e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Instagram App Secret</div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type={showInstagramAppSecret ? "text" : "password"}
          placeholder="Chave secreta Instagram"
          value={instagramAppSecret}
          onChange={(e) => setInstagramAppSecret(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowInstagramAppSecret(!showInstagramAppSecret)} edge="end" size="small">
                  {showInstagramAppSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Redirect URI Instagram</div>
        <div className={classes.redirectBox}>
          <Box style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {redirectUriInstagram}
          </Box>
          <IconButton size="small" onClick={() => handleCopyUri(redirectUriInstagram, "Instagram")}>
            <FileCopy fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.redirectSub}>URI de redirecionamento Instagram</div>
      </Grid>

      {/* ── DIVISOR ── */}
      <Grid item xs={12}>
        <Divider className={classes.blockDivider} />
      </Grid>

      {/* ── BLOCO 3: FACEBOOK ── */}
      <Grid item xs={12}>
        <div className={classes.sectionTitle}>
          🔵 Configurações Facebook
        </div>
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Facebook App ID</div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="ID do aplicativo Facebook"
          value={facebookAppId}
          onChange={(e) => setFacebookAppId(e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Facebook App Secret</div>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type={showFacebookAppSecret ? "text" : "password"}
          placeholder="Chave secreta Facebook"
          value={facebookAppSecret}
          onChange={(e) => setFacebookAppSecret(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowFacebookAppSecret(!showFacebookAppSecret)} edge="end" size="small">
                  {showFacebookAppSecret ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.fieldLabel}>Redirect URI Facebook</div>
        <div className={classes.redirectBox}>
          <Box style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {redirectUriFacebook}
          </Box>
          <IconButton size="small" onClick={() => handleCopyUri(redirectUriFacebook, "Facebook")}>
            <FileCopy fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.redirectSub}>URI de redirecionamento Facebook</div>
      </Grid>

      {!hideSaveButton && (
        <Grid item xs={12} style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={loading}
            style={{ backgroundColor: "#1877F2", color: "#fff", fontWeight: "bold", padding: "10px 32px", fontSize: "0.95rem" }}
          >
            {loading ? "Salvando..." : "Salvar Configurações da Meta"}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const WabaModal = ({ open, onClose, companyId }) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography className={classes.titleText}>Configurações do App Meta</Typography>
          <Typography className={classes.subtitleText}>
            Configure as informações do WhatsApp Oficial, Instagram e Facebook
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers style={{ padding: "24px" }}>
        <WabaForm companyId={companyId} onSaved={onClose} />
      </DialogContent>
    </Dialog>
  );
};

export default WabaModal;
